import {
  Component,
  input,
  OnInit,
  OnDestroy,
  effect,
  signal,
  computed,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CardModule } from 'primeng/card';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProjectResponseDashboardDto } from '../../api/models/project-response-dashboard-dto';
import { ProjectStatsService } from '../../services/project-stats.service';
import { CurrencyFormatPipe } from '../../pipes/currency-format.pipe';

@Component({
  selector: 'app-countdown-progress-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, CardModule, ProgressBarModule, CurrencyFormatPipe],
  templateUrl: './countdown-progress-card.component.html',
  styleUrl: './countdown-progress-card.component.css',
})
export class CountdownProgressCardComponent implements OnInit, OnDestroy {
  protected readonly statsService = inject(ProjectStatsService);

  project = input.required<ProjectResponseDashboardDto>();

  // Compute current active sale info internally
  protected readonly currentSaleInfo = computed(() => {
    const project = this.project();
    if (!project) return null;

    const now = new Date();

    // Check if we're before private sale (upcoming)
    if (project.privateSaleStart) {
      const start = new Date(project.privateSaleStart);

      if (now < start) {
        return {
          type: 'upcoming' as const,
          endDate: start,
          price: project.privateSalePrice,
        };
      }
    }

    // Check Private Sale
    if (project.privateSaleStart && project.privateSaleEnd) {
      const start = new Date(project.privateSaleStart);
      const end = new Date(project.privateSaleEnd);

      if (now >= start && now <= end) {
        return {
          type: 'privateSale' as const,
          endDate: end,
          price: project.privateSalePrice,
        };
      }
    }

    // Check Public Sale
    if (project.publicSaleStart && project.publicSaleEnd) {
      const start = new Date(project.publicSaleStart);
      const end = new Date(project.publicSaleEnd);

      if (now >= start && now <= end) {
        return {
          type: 'publicSale' as const,
          endDate: end,
          price: project.publicSalePrice,
        };
      }
    }

    // Check if TGE is upcoming
    if (project.tgeDate) {
      const tgeDate = new Date(project.tgeDate);

      if (now < tgeDate) {
        return {
          type: 'tge' as const,
          endDate: tgeDate,
          price: project.tgePrice,
        };
      }
    }

    return null;
  });

  // Countdown timer
  protected readonly countdown = signal<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    isExpired: boolean;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: false });

  private countdownInterval?: ReturnType<typeof setInterval>;

  // Computed values
  protected readonly progressPercentage = computed(() => {
    const project = this.project();
    if (!project || !project.softCap) return 0;

    const total = this.statsService.totalRaised();
    const softCap = project.softCap;
    return Math.min(100, Math.round((total / softCap) * 100 * 100) / 100);
  });

  protected readonly softCapPercentage = computed(() => {
    const project = this.project();
    if (!project || !project.softCap || !project.hardCap) return 0;

    const softCap = project.softCap;
    const hardCap = project.hardCap;
    return Math.round((softCap / hardCap) * 100);
  });

  constructor() {
    effect(() => {
      const saleInfo = this.currentSaleInfo();
      if (saleInfo) {
        this.startCountdown();
      }
    });

    // Load stats when project changes (shared with raised-amounts-card)
    effect(() => {
      const project = this.project();
      if (project?.id) {
        this.statsService.loadStats(project.id);
      }
    });
  }

  ngOnInit(): void {
    this.startCountdown();
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  private startCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    const updateCountdown = () => {
      const saleInfo = this.currentSaleInfo();

      if (!saleInfo || !saleInfo.endDate) {
        this.countdown.set({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true });
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        return;
      }

      const now = new Date().getTime();
      const distance = saleInfo.endDate.getTime() - now;

      if (distance < 0) {
        this.countdown.set({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true });
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      this.countdown.set({ days, hours, minutes, seconds, isExpired: false });
    };

    updateCountdown();
    this.countdownInterval = setInterval(updateCountdown, 1000);
  }

  protected isSoftCapReached(): boolean {
    const project = this.project();
    if (!project?.softCap) return false;
    return this.statsService.totalRaised() >= project.softCap;
  }

  protected isHardCapReached(): boolean {
    const project = this.project();
    if (!project?.hardCap) return false;
    return this.statsService.totalRaised() >= project.hardCap;
  }

  protected getCountdownTitle(): string {
    const saleInfo = this.currentSaleInfo();
    if (!saleInfo) return 'projectInsight.countdown.saleEnded';

    switch (saleInfo.type) {
      case 'upcoming':
        return 'projectInsight.countdown.privateSaleStartsIn';
      case 'privateSale':
        return 'projectInsight.countdown.privateSaleEndsIn';
      case 'publicSale':
        return 'projectInsight.countdown.publicSaleEndsIn';
      case 'tge':
        return 'projectInsight.countdown.tgeIn';
      default:
        return 'projectInsight.countdown.saleEnded';
    }
  }
}
